import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export default function CalendarView({ items = [], dateField = 'delivery_date', renderItem, onItemClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const totalDays = lastDay.getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const getItemsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return items.filter(item => {
      const itemDate = item[dateField];
      if (!itemDate) return false;
      return itemDate.startsWith(dateStr);
    });
  };

  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    cells.push(<div key={`empty-${i}`} className="h-28 border-r border-b border-zinc-800/50" />);
  }
  for (let day = 1; day <= totalDays; day++) {
    const dayItems = getItemsForDay(day);
    const todayClass = isToday(day) ? 'bg-amber-500/5 border-amber-500/20' : '';
    cells.push(
      <div key={day} className={`h-28 border-r border-b border-zinc-800/50 p-1.5 overflow-hidden ${todayClass}`}>
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs font-medium ${isToday(day) ? 'text-amber-500 bg-amber-500/10 w-6 h-6 flex items-center justify-center rounded-full' : 'text-zinc-500'}`}>
            {day}
          </span>
          {dayItems.length > 0 && (
            <span className="text-[10px] text-zinc-600">{dayItems.length}</span>
          )}
        </div>
        <div className="space-y-0.5">
          {dayItems.slice(0, 3).map((item, idx) => (
            <button
              key={idx}
              onClick={() => onItemClick && onItemClick(item)}
              className="w-full text-left truncate text-[11px] px-1.5 py-0.5 rounded-sm transition-colors hover:bg-zinc-700/50"
              data-testid={`calendar-item-${item.id}`}
            >
              {renderItem ? renderItem(item) : (
                <span className="text-zinc-300">{item.project_name || item.client_name}</span>
              )}
            </button>
          ))}
          {dayItems.length > 3 && (
            <span className="text-[10px] text-zinc-600 px-1.5">+{dayItems.length - 3}</span>
          )}
        </div>
      </div>
    );
  }
  const remaining = 7 - ((startOffset + totalDays) % 7);
  if (remaining < 7) {
    for (let i = 0; i < remaining; i++) {
      cells.push(<div key={`end-${i}`} className="h-28 border-r border-b border-zinc-800/50" />);
    }
  }

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {MONTHS_FR[month]} {year}
          </h3>
          <Button variant="outline" size="sm" onClick={goToday} className="text-xs border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-sm h-7">
            Aujourd'hui
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={prevMonth} className="w-8 h-8 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800" data-testid="calendar-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={nextMonth} className="w-8 h-8 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800" data-testid="calendar-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-zinc-800">
        {DAYS_FR.map(d => (
          <div key={d} className="py-2 text-center text-xs font-medium text-zinc-500 uppercase tracking-wider border-r border-zinc-800/50 last:border-r-0">
            {d}
          </div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {cells}
      </div>
    </div>
  );
}
